/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

import { Button, InlineNotification, PasswordField, Typography2 } from '@coursera/cds-core';

import Form from 'bundles/authentication-common/components/Form';
import Modal from 'bundles/authentication-common/components/modals/Modal';
import { NOT_EMPTY_CONSTRAINT } from 'bundles/authentication-common/constants/constraints';

import _t from 'i18n!nls/account-settings';

type Props = {
  description?: React.ReactNode;
  isOpen: boolean;
  onCancel?: () => void;
  onConfirm?: (password: string) => Promise<void>;
  submit?: React.ReactNode;
  title?: React.ReactNode;
};

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

const PasswordConfirmationModal = ({
  description = _t('We would like to confirm your password to continue.'),
  isOpen,
  onCancel,
  onConfirm = () => Promise.resolve(),
  submit = _t('Log out from all devices'),
  title = _t('Confirmation'),
}: Props) => {
  // fieldError: shown inline on the PasswordField (wrong password, empty field — user can fix by retyping)
  // serverError: shown as InlineNotification above the form (500/other — not the user's fault)
  const [fieldError, setFieldError] = useState('');
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleConfirm = useCallback(
    async (password: string) => {
      setFieldError('');
      setServerError('');
      setIsLoading(true);
      try {
        await onConfirm(password);
      } catch (e) {
        if (e.status === 401) {
          setFieldError(_t('Password does not match our records.'));
          inputRef.current?.focus();
        } else {
          setServerError(_t('Oops! Something went wrong, try later'));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onConfirm]
  );

  const onSubmit = useCallback(
    (values: Record<string, string>) => {
      handleConfirm(values.password ?? '');
    },
    [handleConfirm]
  );

  const onInvalid = useCallback(() => {
    setFieldError(_t('This field is required.'));
    inputRef.current?.focus();
  }, []);

  const clearErrors = useCallback(() => {
    setFieldError('');
    setServerError('');
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <section css={styles.root}>
        <Typography2 variant="titleMedium" component="h2">
          {title}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {description}
        </Typography2>
        <div role="alert" aria-live="assertive">
          {serverError && <InlineNotification severity="error">{serverError}</InlineNotification>}
        </div>
        <Form
          css={styles.container}
          constraints={{ password: NOT_EMPTY_CONSTRAINT }}
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          hideErrors
        >
          <PasswordField
            name="password"
            label={_t('Password')}
            placeholder={_t('Password')}
            autoComplete="current-password"
            inputRef={inputRef}
            validationStatus={fieldError ? 'error' : undefined}
            validationLabel={fieldError}
            onChange={clearErrors}
          />
          <Button variant="primary" type="submit" fullWidth loading={isLoading}>
            {submit}
          </Button>
        </Form>
      </section>
    </Modal>
  );
};

export default PasswordConfirmationModal;
